import { Directive, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { UrlQueryDef } from '../../_appCommon/base/UrlQueryDef';
import { EntityDataServiceBase } from './EntityDataServiceBase';
import { FormControllerBase } from './FormControllerBase';

export enum CRUDMode { None, Add, Edit }

/**
 * Base Controller Class for CRUD Forms
 *
 */
@Directive()
export class CRUDFormControllerBase<TDataService extends EntityDataServiceBase> extends FormControllerBase<TDataService>
{
  // ********************************************************************
  // #region Properties
  // ********************************************************************

  @Input()
  public limitedCrud: boolean = false;

  /**
  * Id Property - bound to component - to allow consumers of this component to pass in id
  *  If the value is negative - this indicates that the Crud component should go into "add" mode when used
  *  with a crud controller.
  */
  @Input()
  public id: number;

  // ********************************************************************
  // #region UI Binding Properties
  // ********************************************************************


  public title: string;

  public get disableInput(): boolean
  {
    return false;
  }

  protected ___showAddButton: boolean;
  protected ___showDeleteButton: boolean;
  protected ___showUndeleteButton: boolean;
  protected ___showSaveButton: boolean;
  protected ___showCancelButton: boolean;

  public get showAddButton(): boolean
  {
    if (this.___showAddButton && this.formPristine) { return true; }
    else { return false; }
  }

  public get showDeleteButton(): boolean
  {
    if (this.___showDeleteButton && this.formPristine) { return true; }
    else { return false; }
  }

  public get showUndeleteButton(): boolean
  {
    if (this.___showUndeleteButton && this.formPristine) { return true; }
    else { return false; }
  }

  public get showSaveButton(): boolean
  {
    let test = (this.___showSaveButton && this.formDirty && !this.formInvalid);

    if (this.___showSaveButton && this.formDirty && !this.formInvalid)
    {
      return true;
    }
    else
    {
      return false;
    }
  }

  public get showCancelButton(): boolean
  {
    if (this.formDirty) { return true; }
    else { return false; }
  }

  public get showDisplayListButton(): boolean
  {
    if (this.formPristine) { return true; }
    else { return false; }
  }

  public get showRefreshButton(): boolean
  {
    if (this.crudMode === CRUDMode.Edit) { return true; }
    else { return false; }
  }

  public get isFormReady(): boolean
  {
    if (this.crudMode === CRUDMode.Edit) { return true; }
    else { return false; }
  }

  //Properties for HTML view binding
  public CRUDMode_Add = CRUDMode.Add;
  public CRUDMode_Edit = CRUDMode.Edit;

  // *****************************************************
  // Pristine / Dirty / Invalid Properties
  // *****************************************************

  public get formPristine(): boolean
  {
    return !this.formDirty;
  }

  public get formDirty(): boolean
  {
    var formController = this.getFormController(false);
    if (formController)
    {
      return formController.dirty;
    }
    else
    {
      return false;
    }
  }

  public get formInvalid(): boolean
  {
    var formController = this.getFormController(false);
    if (formController)
    {
      return formController.invalid;
    }
    else
    {
      return false;
    }
  }

  // *****************************************************
  // Misc Properties
  // *****************************************************

  public crudMode: CRUDMode;
  public currentEntity: any;
  public currentEntityCopy: any;

  @ViewChild('theForm') public _form: NgForm;

  // #endregion

  // ********************************************************************
  // #region Constructor and Related
  // ********************************************************************

  constructor(
    protected activatedRoute: ActivatedRoute,
    protected dataService: TDataService
  )
  {
    super(dataService);
  }

  ngOnInit()
  {
    this.setCurrentEntity({ id: 0 }); // - We need initialize CurrentEntity to avoid ngForm errors
    this.setCrudMode(CRUDMode.None);


    this.getInitialData();

    super.ngOnInit();
  }

  ngAfterViewInit()
  {

  }

  protected getInitialData()
  {
    // If an id was bound to the component, use it.
    if (this.id)
    {
      this.onIdSelected(this.id);
      return;
    }
  }

  // #endregion

  // ********************************************************************
  // #region Hook Methods
  // ********************************************************************

  protected onGetQueryDefForGetById(queryDef: UrlQueryDef): void { }

  protected onBeforeCreateNewInstance(typeId: number): number { return typeId; }

  protected onAdd(currentEntity: any): void { }
  protected onCancel(): void { }

  protected onBeforeInsert(currentEntity: any): void { }
  protected onAfterInsert(currentEntity: any): void { }

  protected onBeforeUpdate(currentEntity: any): void { }
  protected onAfterUpdate(currentEntity: any): void { }

  protected onBeforeDelete(currentEntity: any): void { }
  protected onAfterDelete(currentEntity: any): void { }

  protected onBeforeUndelete(currentEntity: any): void { }
  protected onAfterUndelete(currentEntity: any): void { }

  // #endregion

  // ********************************************************************
  // #region Event Pub / Sub Methods
  // ********************************************************************

  // ********************************************
  // #region Pub (broadcasters)
  // ********************************************

  protected publishCrudOperationCompleted(alertMessage?: string): void
  {
    this.onCrudComplete.emit(alertMessage);
  }

  // EventEmitter
  @Output()
  public onCrudComplete = new EventEmitter<any>();

  /**
   * Invoked to notify that an entity was selected (called by insert())
   */
  protected publishEntitySelected(): void
  {

  }

  // #endregion

  // ********************************************
  // #region Sub (listeners)
  // ********************************************

  protected registerEventListeners(): void
  {
    // This always gets registered
    this.registerCheckDirtyFormRequest();

    this.registerCrudAddModeRequest();
  }

  /**
   * Fired to ask if there are unsaved changes
   */
  private registerCheckDirtyFormRequest(): void
  {
    let self = this;

  }

  /**
   * Fired to tell this controller to go into add mode.
   */
  private registerCrudAddModeRequest(): void
  {

  }

  /**
   * This is invoked by the SingleRecordViewFormController when any of several events happen that trigger an Id selection.
   *  If the value is negative, this indicates that Add mode should be invoked.
   */
  protected onIdSelected(id: number)
  {
    if (!id || id == 0) { return; }
    if (id < 0) { this.__onAddModeRequest(); }
    else { this.getById(id); }
  }

  protected __onAddModeRequest(): void
  {
    this.add();
  }

  // #endregion

  // *********************************************************
  // #region OVERRIDES
  // *********************************************************

  protected onAfterGetById(_currentEntity: any): void
  {
    this.setCrudMode(CRUDMode.Edit);
  }

  protected setCurrentEntity(_currentEntity: any): void
  {
    this.currentEntity = _currentEntity;
    this.currentEntityCopy = JSON.parse(JSON.stringify(_currentEntity));

    this.setFormPristine();
  }

  protected getByIdCallback(_currentEntity: any)
  {
    this.setCurrentEntity(_currentEntity);
    this.onAfterGetById(_currentEntity);
  }

  protected getQueryDefForGetById(): UrlQueryDef
  {
    var queryDef = new UrlQueryDef();

    this.onGetQueryDefForGetById(queryDef);

    return queryDef;
  }
  // #endregion

  // ********************************************************************
  // #region Core Methods
  // ********************************************************************

  public displayList(alertMessage?: string): void
  {
    this.publishCrudOperationCompleted(alertMessage);
  }

  public add(typeId?: number): void
  {
    this.dataService.checkCanEdit();

    this.onAdd(this.currentEntity);
    this.setCrudMode(CRUDMode.Add);
  }

  public cancel(): void
  {
    this.clearAlert();
    this.setCurrentEntity(this.currentEntityCopy);
    this.onCancel();

    this.publishCrudOperationCompleted();
  }

  public save(): void
  {
    if (this.crudMode === CRUDMode.Add)
    {
      this.insert();
    }
    else if (this.crudMode === CRUDMode.Edit)
    {
      this.update();
    }
  }

  public getById(id: number, clearAlert: boolean = true): void
  {
    this.dataService.getById(id, this.getQueryDefForGetById())
      .subscribe((response: any) =>
      {
        this.clearAlert(clearAlert);
        this.getByIdCallback(response);
      },
      (fail) =>
      {
        this.setDangerAlert(fail.error.errorDetail.message);
      });
  }

  public insert(): void
  {
    this.onBeforeInsert(this.currentEntity);

    this.dataService.insert(this.currentEntity, this.getQueryDefForGetById().getRequestConfig(true))
      .subscribe((response: any) =>
      {
        this.clearAlert();

        let id: number = response.id != undefined ? response.id : response; //sometimes 'response' already have the id

        this.currentEntity.id = id;

        this.dataService.getById(id)
          .subscribe((response: any) =>
          {
            this.getByIdCallback(response);
            this.publishCrudOperationCompleted("Registro agregado");
            this.publishEntitySelected();

            this.onAfterInsert(this.currentEntity);
          });
      },
      (fail) =>
      {
        var outputs = this.CreateErrorMessage(fail.error.failures);
        this.setDangerAlert(fail.error.message + outputs);
      });
  }

  public update(): void
  {
    this.onBeforeUpdate(this.currentEntity);

    this.dataService.update(this.currentEntity, this.getQueryDefForGetById().getRequestConfig(true))
      .subscribe((response: any) =>
      {
        this.clearAlert();
        let id: number = this.currentEntity.id;

        this.dataService.getById(id, this.getQueryDefForGetById())
          .subscribe((response: any) =>
          {
            this.getByIdCallback(response);

            this.publishCrudOperationCompleted("Registro actualizado");

            this.onAfterUpdate(this.currentEntity);
          });
      },
      (fail) =>
      {
        var outputs = this.CreateErrorMessage(fail.error.failures);
        this.setDangerAlert(fail.error.message + outputs);
      });
  }

  public CreateErrorMessage(failures: any): String
  {
    var message = "";

    var array = Object.keys(failures);

    array.forEach(function (key, index)
    {
      message += ", " + key + ":";
      failures[key].forEach(element =>
      {
        message += ' ' + element;
      });
    });

    return message;
  }

  public delete(): void
  {

    let id: number = this.currentEntity.id;

    if (!confirm("Delete?"))
    {
      return;
    }

    this.onBeforeDelete(this.currentEntity);

    this.dataService.delete(id, this.getQueryDefForGetById().getRequestConfig(true))
      .subscribe((response: any) =>
      {
        this.clearAlert();

        this.add();
        this.onAfterDelete(this.currentEntity);
        this.setCurrentEntity(null);

        this.publishCrudOperationCompleted("Registro eliminado");
      },
      (fail) =>
      {
        this.setDangerAlert(fail.error);
      });
  }

  // #endregion

  // *********************************************************
  // #region Support Methods
  // *********************************************************

  protected setCrudMode(crudMode: CRUDMode): void
  {
    this.crudMode = crudMode;

    // *****************
    // Add Button
    // *****************

    if (this.crudMode !== CRUDMode.Add)
    {
      this.___showAddButton = true;
    }
    else { this.___showAddButton = false; }

    // *****************
    // Delete Button
    // *****************

    if (this.crudMode === CRUDMode.Edit)
    {
      if (!(<any>this.currentEntity).isDeleted)
      {
        this.___showDeleteButton = true;
      }
      else { this.___showDeleteButton = false; }

    }
    else { this.___showDeleteButton = false; }

    // *****************
    // Undelete Button
    // *****************

    if (this.crudMode === CRUDMode.Edit && (<any>this.currentEntity).isDeleted)
    {
      this.___showUndeleteButton = true;
    }
    else { this.___showUndeleteButton = false; }

    // *****************
    // Save Button
    // *****************

    if (this.crudMode === CRUDMode.Add || this.crudMode === CRUDMode.Edit)
    {
      this.___showSaveButton = true;
    }
    else { this.___showSaveButton = false; }

    // *****************
    // Cancel Button
    // *****************

    if (this.crudMode === CRUDMode.Add || this.crudMode === CRUDMode.Edit)
    {
      this.___showCancelButton = true;
    }
    else { this.___showCancelButton = false; }
  }

  // #endregion

  // *********************************************************
  // #region Form Dirty related methods
  // *********************************************************

  public setFormPristine(): void
  {
    try
    {
      var formController = this.getFormController(false);
      if (formController) { formController.form.markAsPristine(); }
    }
    catch (e)
    {
      // Ignore
    }
  }

  public setFormDirty(): void
  {
    try
    {
      var formController = this.getFormController(false);
      if (formController) { formController.form.markAsDirty(); }
    }
    catch (e)
    {
      // Ignore
    }
  }

  /**
   * Get the form controller - fail if not found.
   */
  protected getFormController(ignoreFail: boolean = false): NgForm
  {

    if (!this._form)
    {
    }

    return this._form;
  }

  // #endregion
}
